import { useNavigate } from '@tanstack/react-router'

import type {
    ProductLotStockOutNote,
    ProductLotStockOutNoteItem,
} from '@/lib/api/product-lot-api'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useProductLotOutNoteQuery } from '@/hooks/use-product-lot-query'
import { isVirtualLot } from '@/lib/api/sales-order-api'
import { formatQty } from '@/lib/format'
import { twJoin } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

interface Props {
    pxk: ProductLotStockOutNote | null | undefined
}

function statusVariant(
    status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
    switch (status) {
        case 'approved':
            return 'default'
        case 'pending':
            return 'secondary'
        case 'rejected':
        case 'cancelled':
            return 'destructive'
        default:
            return 'outline'
    }
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return ''
    return new Date(iso).toLocaleString('vi-VN')
}

function qtyLabel(item: ProductLotStockOutNoteItem): string {
    const unit = item.product_unit ?? ''
    return unit
        ? `${formatQty(item.quantity)} ${unit}`
        : formatQty(item.quantity)
}

/** Vị trí của dòng trong nhóm các dòng CÙNG sản phẩm của phiếu. */
interface LotIndex {
    index: number
    total: number
}

/**
 * Gắn chỉ số lô cho từng dòng: một sản phẩm bán từ nhiều lô sẽ thành nhiều dòng
 * TRÙNG TÊN, chỉ khác mã lô chữ nhỏ — nhìn rất giống lỗi lặp. Nhóm nào có từ 2
 * dòng trở lên thì mỗi dòng được đánh "Lô 1/3, 2/3…" để đọc lướt vẫn hiểu ngay.
 *
 * Nhóm theo product_id; thiếu product_id thì rơi về tên sản phẩm, cuối cùng là
 * id dòng (khi đó dòng đứng một mình, không đánh số).
 */
function withLotIndex(
    items: ProductLotStockOutNoteItem[],
): Array<{ item: ProductLotStockOutNoteItem; lotIndex?: LotIndex }> {
    const keyOf = (it: ProductLotStockOutNoteItem) =>
        it.product_id ?? it.product_name ?? it.id
    const totals = new Map<string, number>()
    for (const it of items) {
        const key = keyOf(it)
        totals.set(key, (totals.get(key) ?? 0) + 1)
    }
    const seen = new Map<string, number>()
    return items.map((item) => {
        const key = keyOf(item)
        const total = totals.get(key) ?? 1
        if (total < 2) return { item }
        const index = (seen.get(key) ?? 0) + 1
        seen.set(key, index)
        return { item, lotIndex: { index, total } }
    })
}

/**
 * Một dòng hàng của phiếu xuất. Không có phần tử bấm được bên trong: cả thẻ đã
 * là nút điều hướng sang phiếu, lồng thêm link/nút vào là bẫy bàn phím và click.
 *
 * Dòng NỢ LÔ (lô ảo, FD-372) KHÔNG hiện mã lô: mã lô ảo không dán trên kệ nào,
 * hiện ra chỉ khiến thủ kho đi tìm một thứ không tồn tại. Thay bằng lời nhắc
 * "chờ nhập bù lô", kèm nhãn chữ chứ không chỉ đổi màu.
 */
function PxkItemRow({
    item,
    lotIndex,
}: {
    item: ProductLotStockOutNoteItem
    lotIndex?: LotIndex
}) {
    const owed = isVirtualLot(item)
    const meta = owed
        ? m.orders_pxk_item_debt_hint()
        : [item.product_lot_code ?? item.product_lot_id, item.position_code]
              .filter(Boolean)
              .join(' · ')
    return (
        <li
            data-testid={`pxk-item-${item.id}`}
            className="flex items-start justify-between gap-3 py-2"
        >
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium break-words">
                        {item.product_name ?? '—'}
                    </span>
                    {lotIndex && (
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                            {m.orders_pxk_item_lot_index({
                                index: String(lotIndex.index),
                                total: String(lotIndex.total),
                            })}
                        </span>
                    )}
                    {owed && (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            {m.orders_pxk_item_debt_badge()}
                        </Badge>
                    )}
                </div>
                <p
                    className={twJoin(
                        'text-xs break-words',
                        owed
                            ? 'text-amber-700 dark:text-amber-300'
                            : 'font-mono text-muted-foreground',
                    )}
                >
                    {meta}
                </p>
            </div>
            <span
                data-testid="pxk-row-qty"
                className="shrink-0 font-semibold tabular-nums"
            >
                {qtyLabel(item)}
            </span>
        </li>
    )
}

export function OrderPXKCard({ pxk }: Props) {
    const navigate = useNavigate()
    // Danh sách dòng hàng chỉ có ở API chi tiết — bản list của đơn không trả
    // items. Hook tự tắt khi chưa có phiếu nên vẫn gọi được trước early return.
    const { data, isLoading, isError } = useProductLotOutNoteQuery(pxk?.id)
    if (!pxk) return null
    // BE bỏ hẳn khoá `items` khi phiếu không có dòng nào (json omitempty), nên
    // phải phân biệt "chưa tải xong" (undefined) với "tải xong, phiếu rỗng" —
    // gộp hai cái làm một thì phiếu rỗng hiện ra một mảng gạch trống trơn.
    const note = data?.data
    const items = note ? (note.items ?? []) : undefined
    return (
        <Card
            role="button"
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() =>
                void navigate({
                    to: '/sales-warehouse/stock-out-notes/$id',
                    params: { id: pxk.id },
                })
            }
        >
            <CardHeader>
                <CardTitle className="text-base">
                    {m.orders_pxk_card_title()}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">{pxk.code}</span>
                    <Badge variant={statusVariant(pxk.status)}>
                        {pxk.status}
                    </Badge>
                </div>
                <div>
                    <span className="text-muted-foreground">
                        {m.orders_pxk_created_at()}:{' '}
                    </span>
                    {formatDate(pxk.created_at)}
                </div>
                {pxk.approved_at && (
                    <div>
                        <span className="text-muted-foreground">
                            {m.orders_pxk_approved_at()}:{' '}
                        </span>
                        {formatDate(pxk.approved_at)}
                    </div>
                )}
                {/* Gạch ngăn chỉ vẽ khi phía dưới thật sự có nội dung — không
                    để lại một đường kẻ trống dưới đáy thẻ. */}
                {(isLoading || isError || items) && (
                    <div className="border-t pt-2">
                        {isLoading && (
                            <p className="text-xs text-muted-foreground">
                                {m.orders_pxk_items_loading()}
                            </p>
                        )}
                        {!isLoading && isError && (
                            <p className="text-xs text-destructive">
                                {m.orders_pxk_items_error()}
                            </p>
                        )}
                        {!isLoading && !isError && items && (
                            <>
                                <p className="text-xs font-medium text-muted-foreground">
                                    {m.orders_pxk_items_title({
                                        count: String(items.length),
                                    })}
                                </p>
                                {items.length === 0 ? (
                                    <p className="pt-1 text-xs text-muted-foreground">
                                        {m.orders_pxk_items_empty()}
                                    </p>
                                ) : (
                                    <ul className="divide-y">
                                        {withLotIndex(items).map(
                                            ({ item, lotIndex }) => (
                                                <PxkItemRow
                                                    key={item.id}
                                                    item={item}
                                                    lotIndex={lotIndex}
                                                />
                                            ),
                                        )}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}
